import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getMarketingConsent, isMarketingPath, onMarketingConsentChange } from '../../lib/consent';

/**
 * Google Analytics 4 — mismas reglas que MetaPixel.tsx: solo con consentimiento,
 * solo en producción y solo en rutas públicas de marketing.
 *
 * - SPA: el page_view se dispara a mano en cada cambio de ruta (send_page_view: false).
 *   En GA4 hay que apagar "Page changes based on browser history events" en
 *   Enhanced measurement, si no se duplican las visitas.
 * - Fuera de la allowlist (/panel, /pagar, /activar-cuenta…) se activa
 *   window['ga-disable-<ID>'], así gtag no envía nada aunque ya esté cargado.
 * - page_location sin hash y solo con parámetros de campaña (utm_*, gclid):
 *   nada de tokens ni IDs internos.
 * - Google Signals y personalización de anuncios apagados.
 */

const GA_ID = 'G-J0BS48P7PD';
const DISABLE_FLAG = `ga-disable-${GA_ID}`;
const KEPT_PARAMS = /^(utm_[a-z]+|gclid|gbraid|wbraid)$/;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const setGaDisabled = (disabled: boolean) => {
  (window as unknown as Record<string, unknown>)[DISABLE_FLAG] = disabled;
};

const isProductionHost = () => window.location.hostname.endsWith('conectemoschile.cl');

function loadGtag() {
  if (window.gtag) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js exige el objeto `arguments`, no un array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };

  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
}

function safePageLocation() {
  const { origin, pathname, search } = window.location;
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (KEPT_PARAMS.test(key)) kept.append(key, value);
  });
  const query = kept.toString();
  return `${origin}${pathname}${query ? `?${query}` : ''}`;
}

function clearGaCookies() {
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_'));
  const domains = ['', `; domain=${window.location.hostname}`, '; domain=.conectemoschile.cl'];
  names.forEach((name) =>
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d}`;
    })
  );
}

export default function GoogleAnalytics() {
  const { pathname } = useLocation();
  const [consent, setConsent] = useState(getMarketingConsent);

  useEffect(
    () =>
      onMarketingConsentChange((value) => {
        setConsent(value);
        if (value === 'denied' && window.gtag) {
          window.gtag('consent', 'update', { analytics_storage: 'denied' });
          setGaDisabled(true);
          clearGaCookies();
        } else if (value === 'granted' && window.gtag) {
          window.gtag('consent', 'update', { analytics_storage: 'granted' });
        }
      }),
    []
  );

  useEffect(() => {
    const allowed = consent === 'granted' && isProductionHost() && isMarketingPath(pathname);
    setGaDisabled(!allowed);
    if (!allowed) return;

    loadGtag();
    // Esperar a que Helmet actualice el <title> de la nueva ruta.
    const timer = window.setTimeout(() => {
      window.gtag?.('event', 'page_view', {
        page_location: safePageLocation(),
        page_path: pathname,
        page_title: document.title,
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [pathname, consent]);

  return null;
}
